import { useEffect, useState } from 'react'
import {
  addRenewalNote, createRenewalQuote, fetchHsOptions, fetchRenewalEvents, fetchRenewals,
  fetchTemplates, hubspotDealUrl, linkRenewalDeal, linkRenewalQuote, markRenewalAtRisk, markRenewalRenewedApi,
  recordRenewalLost, recordRenewalRollover, reopenRenewal, searchRenewalDeals, searchRenewalQuotes,
  type HsOptions, type RenewalDealHit, type RenewalEvent, type RenewalQuoteHit, type RenewalRow, type RenewalsResponse,
  type RenewalQuoteResult, type TemplateRow,
} from '../lib/api.js'
import { SlideOver } from '../components/SlideOver.js'
import {
  ContractFacts, ContractLines, OUTCOME_PILL, STAGE_LABELS,
} from '../components/ContractSummary.js'
import { KpiTile } from '../components/KpiTile.js'
import { daysUntil, formatUKDate } from '@shared/dates'
import { formatGBP } from '@shared/money'

/**
 * Renewals (decision 105) — the contract book coming up for renewal, mirrored
 * from HubSpot's "Active Contracts / Renewals" pipeline by the sweep.
 *
 * Each row pairs the contract deal being renewed with its NEW quote — a W9R
 * portal quote — or a linked existing Sales Pipeline deal (quotes made the old
 * way). Outcomes: rolled over (stage move + admin email), renewed (automatic
 * when the W9R quote is accepted; manual for linked deals), not renewing
 * (stage move + the open quote withdrawn).
 *
 * ⚠️ Staff only — the nav entry is staffOnly and every endpoint refuses
 * partner sessions.
 */


/** The stage labels worth translating for the table — ids otherwise. */

/**
 * Decision 112: the tile buckets, shared with the cockpit's renewals KPIs so
 * the two surfaces can never disagree about what "due now" means.
 */
export function renewalBuckets(rows: RenewalRow[], windowDays: number) {
  const open = rows.filter((r) => r.outcome === 'open')
  const closed = rows.filter((r) => r.outcome !== 'open')
  const dLeft = (r: RenewalRow) => (r.contractEndDate ? daysUntil(r.contractEndDate) : null)
  return {
    open,
    closed,
    dueNow: open.filter((r) => { const d = dLeft(r); return d == null || d <= windowDays }),
    far: open.filter((r) => { const d = dLeft(r); return d != null && d > windowDays }),
    due40: open.filter((r) => { const d = dLeft(r); return d != null && d <= 40 }),
    noQuote: open.filter((r) => !r.quote && !r.newDealId),
  }
}

export function Renewals({ onOpen, initialTile, initialOwner, initialOpenId }: {
  onOpen: (quoteId: string) => void
  /** Decision 112: cockpit tiles open this page pre-filtered. */
  initialTile?: string
  initialOwner?: string
  initialOpenId?: string
}) {
  const [data, setData] = useState<RenewalsResponse | null>(null)
  const [hsOpts, setHsOpts] = useState<HsOptions | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [openId, setOpenId] = useState<string | null>(initialOpenId ?? null)
  const [showClosed, setShowClosed] = useState(false)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)
  const [tileFilter, setTileFilter] = useState<string | null>(initialTile ?? null)
  const [ownerFilter, setOwnerFilter] = useState(initialOwner ?? 'all')
  const PAGE_SIZE = 25

  const load = () => { fetchRenewals().then(setData).catch((e: Error) => setError(e.message)) }
  useEffect(load, [])
  useEffect(() => { fetchHsOptions().then(setHsOpts).catch(() => {}) }, [])

  const rows = data?.renewals ?? []
  const windowDays = data?.windowDays ?? 120
  // The buckets behind the tiles (Mark, 2026-08-07): "Open renewals" is the
  // <121-day set (no-date rows count here — they came in via a due stage and
  // need attention); the far bucket is the Final-Year pulls whose date is
  // still beyond the window. Tiles are click-to-filter. Bucket logic is the
  // exported renewalBuckets — the cockpit's renewals KPIs share it (112).
  const { open, closed, dueNow, far, due40, noQuote } = renewalBuckets(rows, windowDays)
  const dLeft = (r: RenewalRow) => (r.contractEndDate ? daysUntil(r.contractEndDate) : null)

  const tileRows: Record<string, RenewalRow[]> = {
    dueNow, far, due40, noQuote, concluded: closed,
  }
  const owners = [...new Set(rows.map((r) => r.ownerName).filter((x): x is string => !!x))].sort()
  const base = tileFilter ? tileRows[tileFilter] : (showClosed ? rows : open)
  const filtered = base.filter((r) => {
    if (ownerFilter === 'none' && r.ownerName) return false
    if (ownerFilter !== 'all' && ownerFilter !== 'none' && r.ownerName !== ownerFilter) return false
    const q = query.trim().toLowerCase()
    if (!q) return true
    return [r.dealName, r.customerName, r.quote?.number, r.hsDealId, r.ownerName]
      .some((v) => v?.toLowerCase().includes(q))
  })
  // Pages of 25 (Mark, 2026-08-07) — over the FILTERED set, so search still
  // reaches everything; filters/search reset to page 1.
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE)
  const selected = rows.find((r) => r.id === openId) ?? null

  const pickTile = (key: string) => {
    setTileFilter((cur) => (cur === key ? null : key))
    setPage(0)
  }

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    setBusy(true)
    try { await fn(); load() } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  if (error && !data) return <p className="pill pill--stop">{error}</p>
  if (!data) return <p className="muted">Loading…</p>

  const daysPill = (r: RenewalRow) => {
    const d = dLeft(r)
    if (d == null) return <span className="pill tiny">no end date</span>
    const label = d < 0 ? `${-d}d overdue` : d === 0 ? 'today' : `${d}d`
    const cls = d <= 7 ? 'pill pill--stop tiny' : d <= 40 ? 'pill pill--warn tiny' : 'pill tiny'
    return <span className={cls}>{label}</span>
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Renewals</h1>
          <p>
            Contracts inside the {data.windowDays}-day renewal window, mirrored from the Active
            Contracts / Renewals pipeline every sweep. Each row tracks the contract being renewed
            and the new quote that replaces it — or records a rollover.
          </p>
        </div>
      </div>

      {error && <div className="banner banner--stop"><strong>That didn't work.</strong><span>{error}</span></div>}

      <div className="tiles">
        {([
          { key: 'dueNow', label: 'Open renewals', value: dueNow.length, sub: `inside ${windowDays} days` },
          { key: 'far', label: `${windowDays} days – 1 year`, value: far.length, sub: 'in a renewal stage, date further out' },
          { key: 'due40', label: 'Due inside 40 days', value: due40.length, sub: 'chase now' },
          { key: 'noQuote', label: 'Nothing quoted yet', value: noQuote.length, sub: 'no quote or linked deal' },
          { key: 'concluded', label: 'Concluded', value: closed.length, sub: 'rolled over · renewed · lost' },
        ] as const).map((t) => (
          <div key={t.key} role="button" tabIndex={0} style={{ cursor: 'pointer' }}
            title={tileFilter === t.key ? 'Click to clear this filter' : 'Click to filter the table to these'}
            onClick={() => pickTile(t.key)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') pickTile(t.key) }}>
            <KpiTile label={t.label} value={String(t.value)}
              sub={tileFilter === t.key ? 'filtering — click to clear' : t.sub}
              accent={tileFilter === t.key} />
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card__head">
          <div className="toolbar">
            <input className="input" placeholder="Search renewals…" value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(0) }} />
            {owners.length > 0 && (
              <select className="select" value={ownerFilter} title="Filter by the contract deal's owner"
                onChange={(e) => { setOwnerFilter(e.target.value); setPage(0) }}>
                <option value="all">All owners</option>
                <option value="none">No owner</option>
                {owners.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            <label className={`chip chip--action ${showClosed && !tileFilter ? 'chip--on' : ''}`} style={{ cursor: 'pointer' }}>
              <input type="checkbox" style={{ display: 'none' }} checked={showClosed && !tileFilter}
                onChange={(e) => { setShowClosed(e.target.checked); setTileFilter(null); setPage(0) }} />
              {showClosed && !tileFilter ? 'Hiding nothing' : 'Show concluded'}
            </label>
            {tileFilter && (
              <button className="chip chip--action chip--on" onClick={() => { setTileFilter(null); setPage(0) }}
                title="Clear the tile filter">✕ Clear tile filter</button>
            )}
          </div>
          <span className="muted small">
            {filtered.length === 0
              ? `0 of ${rows.length}`
              : `${safePage * PAGE_SIZE + 1}–${Math.min((safePage + 1) * PAGE_SIZE, filtered.length)} of ${filtered.length}`}
          </span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Contract</th>
                <th>Customer</th>
                <th>Owner</th>
                <th>Ends</th>
                <th>Stage</th>
                <th className="num">Value</th>
                <th>New quote / deal</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => (
                <tr key={r.id} onClick={() => setOpenId(r.id)} title="Open this renewal">
                  {/* Decision 143: the contract deal is a door into HubSpot. */}
                  <td style={{ fontWeight: 600 }}>
                    {hubspotDealUrl(hsOpts?.portalId ?? null, r.hsDealId) ? (
                      <a href={hubspotDealUrl(hsOpts?.portalId ?? null, r.hsDealId)!} target="_blank" rel="noreferrer"
                        title="Open the contract deal in HubSpot" onClick={(e) => e.stopPropagation()}>
                        {r.dealName ?? `(deal ${r.hsDealId})`} ↗
                      </a>
                    ) : (r.dealName ?? `(deal ${r.hsDealId})`)}
                  </td>
                  <td>{r.customerName ?? '—'}</td>
                  <td className="muted">{r.ownerName ?? '—'}</td>
                  <td className="muted">
                    {r.contractEndDate ? formatUKDate(r.contractEndDate) : '—'}{' '}
                    {r.outcome === 'open' && daysPill(r)}
                  </td>
                  <td className="muted">{r.dealStage ? (STAGE_LABELS[r.dealStage] ?? r.dealStage) : '—'}</td>
                  <td className="num">{r.dealAmount != null ? formatGBP(r.dealAmount) : '—'}</td>
                  <td>
                    {r.quote ? (
                      <button className="nav__link" style={{ fontWeight: 600, color: 'var(--w9-blue)', padding: 0 }}
                        title="Open the renewal quote in the builder"
                        onClick={(e) => { e.stopPropagation(); onOpen(r.quote!.id) }}>
                        {r.quote.number} <span className="tiny muted">({r.quote.status})</span>
                      </button>
                    ) : r.newDealId ? (
                      // Decision 143: the linked new deal opens in HubSpot too.
                      hubspotDealUrl(hsOpts?.portalId ?? null, r.newDealId) ? (
                        <a className="small" href={hubspotDealUrl(hsOpts?.portalId ?? null, r.newDealId)!} target="_blank" rel="noreferrer"
                          title="Open the linked deal in HubSpot" onClick={(e) => e.stopPropagation()}>
                          deal {r.newDealId} (linked) ↗
                        </a>
                      ) : <span className="muted small">deal {r.newDealId} (linked)</span>
                    ) : (
                      <span className="pill pill--warn tiny">nothing yet</span>
                    )}
                  </td>
                  <td>
                    <span className={OUTCOME_PILL[r.outcome].cls}>{OUTCOME_PILL[r.outcome].label}</span>
                    {r.outcome !== 'open' && !r.hsMoveDone && (
                      <span className="pill pill--warn tiny" style={{ marginLeft: 6 }}
                        title={r.hsLastError ?? 'The HubSpot stage move has not landed yet — the sweep retries it.'}>HS pending</span>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="muted" style={{ padding: 20 }}>
                  {rows.length === 0
                    ? 'Nothing inside the renewal window yet — the sweep mirrors the Active Contracts / Renewals pipeline every 10 minutes (or run "Renewals mirror" from Scheduled jobs).'
                    : 'Nothing matches.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {pageCount > 1 && (
          <div className="card__body" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, paddingTop: 8 }}>
            <button className="btn btn--sm" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>← Prev</button>
            <span className="muted small">Page {safePage + 1} of {pageCount}</span>
            <button className="btn btn--sm" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}>Next →</button>
          </div>
        )}
      </div>

      {selected && (
        <RenewalSlideOver
          renewal={selected}
          lostReasons={data.lostReasons}
          portalId={hsOpts?.portalId ?? null}
          busy={busy}
          onClose={() => setOpenId(null)}
          onOpenQuote={onOpen}
          run={run}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// The row's slide-over: details, the old deal's lines, the four actions.
// ---------------------------------------------------------------------------

function RenewalSlideOver({
  renewal: r, lostReasons, portalId, busy, onClose, onOpenQuote, run,
}: {
  renewal: RenewalRow
  lostReasons: Array<{ key: string; label: string }>
  portalId: string | null
  busy: boolean
  onClose: () => void
  onOpenQuote: (quoteId: string) => void
  run: (fn: () => Promise<unknown>) => Promise<void>
}) {
  const [panel, setPanel] = useState<'summary' | 'rollover' | 'lost' | 'link' | 'linkQuote' | 'activity' | 'atRisk' | 'template'>('summary')
  const [notice, setNotice] = useState<string | null>(null)
  // Decision 124: the template chooser. `templateFor` remembers WHY we opened
  // it — a plain new quote, or a "Link + quote" on a specific deal — so the
  // same panel serves both entry points and neither loses its deal.
  const [templateFor, setTemplateFor] = useState<{ dealId: string; dealName: string } | null>(null)
  const [templates, setTemplates] = useState<TemplateRow[] | null>(null)

  // At-risk form (105 amended — the note is REQUIRED, it's the point)
  const [atRiskNote, setAtRiskNote] = useState('')
  // Rollover form
  const [rolloverNote, setRolloverNote] = useState('')
  const [newEndDate, setNewEndDate] = useState('')
  // Lost form
  const [lostReason, setLostReason] = useState('')
  const [lostDetail, setLostDetail] = useState('')
  // Link form
  const [dealQuery, setDealQuery] = useState('')
  const [dealResults, setDealResults] = useState<RenewalDealHit[]>([])
  // Link-quote form (decision 188) — the forgot-to-link fix-up
  const [quoteQuery, setQuoteQuery] = useState('')
  const [quoteResults, setQuoteResults] = useState<RenewalQuoteHit[]>([])
  const [events, setEvents] = useState<RenewalEvent[] | null>(null)
  // Notes (portal-only working memory — never synced)
  const [noteText, setNoteText] = useState('')
  const [noteBusy, setNoteBusy] = useState(false)

  // Templates load when the chooser opens — staff sessions act as Wave 9
  // Direct, so this list is already "Wave9-global + Wave 9 Direct's own".
  useEffect(() => {
    if (panel !== 'template' || templates != null) return
    fetchTemplates().then((res) => setTemplates(res.templates.filter((t) => t.active)))
      .catch(() => setTemplates([]))
  }, [panel, templates])

  useEffect(() => {
    if (panel !== 'link' || dealQuery.trim().length < 2) { setDealResults([]); return }
    const t = setTimeout(() => {
      searchRenewalDeals(dealQuery).then((res) => setDealResults(res.deals)).catch(() => setDealResults([]))
    }, 250)
    return () => clearTimeout(t)
  }, [panel, dealQuery])

  useEffect(() => {
    if (panel !== 'linkQuote' || quoteQuery.trim().length < 2) { setQuoteResults([]); return }
    const t = setTimeout(() => {
      searchRenewalQuotes(quoteQuery).then((res) => setQuoteResults(res.quotes)).catch(() => setQuoteResults([]))
    }, 250)
    return () => clearTimeout(t)
  }, [panel, quoteQuery])

  /**
   * Decision 124: one path for both entry points. A template is optional; the
   * deal (from "Link + quote") rides along when there is one. Skipped lines and
   * retired T&Cs are surfaced on the way into the builder rather than silently
   * swallowed — a quote with no terms must be visible before it is sent.
   */
  const startRenewalQuote = (templateId: string | null) => run(async () => {
    const res: RenewalQuoteResult = templateFor
      ? (await linkRenewalDeal(r.id, templateFor.dealId, true, templateId)) as RenewalQuoteResult
      : await createRenewalQuote(r.id, templateId)
    if (!res.id) return
    const warnings = [
      res.retiredTerms ? `its T&Cs (${res.retiredTerms}) are retired — pick a set before sending` : null,
      res.skipped && res.skipped.length > 0 ? `${res.skipped.length} line(s) skipped: ${res.skipped.join(', ')}` : null,
      res.adhocCarried && res.adhocCarried.length > 0 ? `${res.adhocCarried.length} ad-hoc line(s) at saved prices — review` : null,
    ].filter(Boolean)
    if (warnings.length > 0) setNotice(`Created ${res.quoteNumber} — ${warnings.join('; ')}.`)
    onOpenQuote(res.id)
  })

  // Events load with the slide-over — the summary shows the notes.
  useEffect(() => {
    fetchRenewalEvents(r.id).then((res) => setEvents(res.events)).catch(() => setEvents([]))
  }, [r.id])

  const notes = (events ?? []).filter((e) => e.kind === 'note' || e.kind === 'marked_at_risk')

  const submitNote = async () => {
    const text = noteText.trim()
    if (!text) return
    setNoteBusy(true)
    try {
      await addRenewalNote(r.id, text)
      setNoteText('')
      const res = await fetchRenewalEvents(r.id)
      setEvents(res.events)
    } catch (e) {
      setNotice(`The note didn't save: ${(e as Error).message}`)
    } finally {
      setNoteBusy(false)
    }
  }


  return (
    <SlideOver
      title={r.dealName ?? `Deal ${r.hsDealId}`}
      subtitle={r.customerName ?? undefined}
      onClose={onClose}
      footer={panel === 'summary' ? (
        <div className="rowflex" style={{ justifyContent: 'space-between', width: '100%' }}>
          <button className="btn btn--sm" onClick={() => setPanel('activity')}>Activity</button>
          {r.outcome !== 'open' && (
            <button className="btn btn--sm" disabled={busy}
              title="Reopens the portal record only — a stage move already made in HubSpot stays put."
              onClick={() => run(() => reopenRenewal(r.id))}>Reopen</button>
          )}
        </div>
      ) : (
        <button className="btn btn--sm" onClick={() => { setPanel('summary'); setNotice(null) }}>← Back</button>
      )}
    >
      {notice && <div className="banner"><span>{notice}</span></div>}

      {panel === 'summary' && (
        <>
          {/* Decision 127: the contract block is shared with the Builder's
              read-only Contract panel — one component, no drift. */}
          <ContractFacts r={r} portalId={portalId} extraFacts={r.quote ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderBottom: '1px solid var(--line-soft)' }}>
              <span className="muted small">Renewal quote</span>
              <span className="small" style={{ textAlign: 'right' }}>
                <button className="nav__link" style={{ color: 'var(--w9-blue)', fontWeight: 600, padding: 0 }}
                  onClick={() => onOpenQuote(r.quote!.id)}>{r.quote.number} ({r.quote.status})</button>
              </span>
            </div>
          ) : undefined} />
          <ContractLines lines={r.lineItems} />

          <div style={{ marginTop: 14 }}>
            <h3 className="small" style={{ margin: '0 0 6px' }}>Notes <span className="muted">(portal-only — never synced anywhere)</span></h3>
            {notes.map((n, i) => (
              <div key={i} className="small" style={{ padding: '5px 0', borderBottom: '1px solid var(--line-soft)' }}>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {n.kind === 'marked_at_risk' && <span className="pill pill--warn tiny" style={{ marginRight: 6 }}>at risk</span>}
                  {String(n.detail.text ?? n.detail.note ?? '')}
                </div>
                <div className="tiny muted">{n.actor ?? 'someone'} · {formatUKDate(n.at)}</div>
              </div>
            ))}
            {events != null && notes.length === 0 && <p className="tiny muted" style={{ margin: '2px 0 6px' }}>Nothing yet.</p>}
            <div className="rowflex" style={{ marginTop: 6, alignItems: 'flex-start' }}>
              <textarea className="input" rows={2} style={{ flex: 1 }} placeholder="e.g. Spoke to the bursar — waiting on governors, call back w/c 24th"
                value={noteText} onChange={(e) => setNoteText(e.target.value)} />
              <button className="btn btn--sm" disabled={noteBusy || !noteText.trim()} onClick={() => void submitNote()}>
                {noteBusy ? 'Saving…' : 'Add note'}
              </button>
            </div>
          </div>

          {r.outcome === 'open' && (
            <div style={{ marginTop: 16 }}>
              <div className="rowflex" style={{ flexWrap: 'wrap', gap: 8 }}>
                {!r.quote && !r.newDealId && (
                  <button className="btn btn--primary" disabled={busy}
                    title="Start the W9R quote — blank, or from one of your templates."
                    onClick={() => { setTemplateFor(null); setPanel('template') }}>Create renewal quote</button>
                )}
                {r.quote && (
                  <button className="btn" onClick={() => onOpenQuote(r.quote!.id)}>Open {r.quote.number}</button>
                )}
                {!r.quote && (
                  <button className="btn" disabled={busy} onClick={() => setPanel('linkQuote')}
                    title="The renewal quote was built in the ordinary builder and never linked here — find it and this renewal adopts it. An accepted quote closes the loop straight away.">
                    Link existing quote
                  </button>
                )}
                {!r.quote && (
                  <button className="btn" disabled={busy} onClick={() => setPanel('link')}
                    title="The new quote was made outside the portal — track its Sales Pipeline deal here.">
                    {r.newDealId ? 'Re-link deal' : 'Link existing deal'}
                  </button>
                )}
                {(r.quote || r.newDealId) && (
                  <button className="btn" disabled={busy}
                    title="Marks the contract renewed by hand — the accepted W9R quote does this automatically."
                    onClick={() => run(() => markRenewalRenewedApi(r.id))}>Mark renewed</button>
                )}
                <button className="btn" style={{ color: 'var(--warn, #b45309)' }} disabled={busy}
                  title="Moves the contract deal to Renewal at Risk — with a note saying why."
                  onClick={() => setPanel('atRisk')}>Renewal at risk</button>
                <button className="btn" disabled={busy} onClick={() => setPanel('rollover')}>Rolled over</button>
                <button className="btn btn--danger" disabled={busy} onClick={() => setPanel('lost')}>Not renewing</button>
              </div>
              <p className="tiny muted" style={{ marginTop: 8 }}>
                Accepting the W9R quote records the renewal automatically — the old deal moves to
                “New Contract - In PRJ” and both deals carry the association.
              </p>
            </div>
          )}
        </>
      )}

      {panel === 'atRisk' && (
        <>
          <div className="banner banner--warn">
            <strong>Renewal at risk.</strong>
            <span>
              Moves the contract deal to “Renewal at Risk” in HubSpot and records why — the note is
              required, so the next person picking this up sees the story. The renewal stays open.
            </span>
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label>Why is it at risk?</label>
            <textarea className="input" rows={3} style={{ width: '100%' }} autoFocus
              placeholder="e.g. Head considering a MAT-wide deal with another supplier — decision expected end of month"
              value={atRiskNote} onChange={(e) => setAtRiskNote(e.target.value)} />
          </div>
          <div className="rowflex" style={{ marginTop: 12 }}>
            <button className="btn btn--primary" disabled={busy || !atRiskNote.trim()}
              onClick={() => run(async () => {
                const res = await markRenewalAtRisk(r.id, atRiskNote.trim())
                setAtRiskNote('')
                setEvents(null)
                fetchRenewalEvents(r.id).then((ev) => setEvents(ev.events)).catch(() => setEvents([]))
                setPanel('summary')
                setNotice(res.moved
                  ? 'Marked at risk — the deal has moved in HubSpot.'
                  : `Marked at risk in the portal. ⚠️ The HubSpot stage move didn't happen: ${res.moveError ?? 'unknown reason'}`)
              })}>Mark at risk</button>
          </div>
        </>
      )}

      {panel === 'rollover' && (
        <>
          <div className="banner">
            <strong>Contract rolls over.</strong>
            <span>
              No new deal, no line changes — the term extends and the end date resets. The old deal
              moves to “Process Rollover” and the admin mailbox is emailed to extend, reset and invoice.
            </span>
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label>New contract end date <span className="muted">(optional — rides the email)</span></label>
            <input className="input" type="date" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} />
          </div>
          <div className="field" style={{ marginTop: 8 }}>
            <label>Note <span className="muted">(optional — e.g. “36 → 48 months, agreed with the head”)</span></label>
            <textarea className="input" rows={3} style={{ width: '100%' }} value={rolloverNote} onChange={(e) => setRolloverNote(e.target.value)} />
          </div>
          <div className="rowflex" style={{ marginTop: 12 }}>
            <button className="btn btn--primary" disabled={busy}
              onClick={() => run(async () => {
                const res = await recordRenewalRollover(r.id, {
                  note: rolloverNote.trim() || undefined, newEndDate: newEndDate || undefined,
                })
                setPanel('summary')
                setNotice(res.emailSent
                  ? 'Rollover recorded — the admin mailbox has been emailed.'
                  : `Rollover recorded. ⚠️ The email did not go out: ${res.emailError ?? 'unknown reason'}`)
              })}>Record rollover</button>
          </div>
        </>
      )}

      {panel === 'lost' && (
        <>
          <div className="banner banner--stop">
            <strong>Customer is not renewing.</strong>
            <span>
              The old deal moves to “Renewal Lost - Cancel Existing Services” for the project team;
              any open renewal quote is withdrawn in the same action.
            </span>
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label>Reason</label>
            <select className="select" style={{ width: '100%' }} value={lostReason} onChange={(e) => setLostReason(e.target.value)}>
              <option value="">Pick a reason…</option>
              {lostReasons.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
            </select>
          </div>
          <div className="field" style={{ marginTop: 8 }}>
            <label>Detail <span className="muted">(optional)</span></label>
            <textarea className="input" rows={3} style={{ width: '100%' }} value={lostDetail} onChange={(e) => setLostDetail(e.target.value)} />
          </div>
          <div className="rowflex" style={{ marginTop: 12 }}>
            <button className="btn btn--danger" disabled={busy || !lostReason}
              onClick={() => run(async () => {
                const res = await recordRenewalLost(r.id, { reason: lostReason, detail: lostDetail.trim() || undefined })
                setPanel('summary')
                setNotice(res.quoteWithdrawn ? 'Recorded — the open renewal quote was withdrawn too.' : 'Recorded.')
              })}>Record — not renewing</button>
          </div>
        </>
      )}

      {panel === 'link' && (
        <>
          <div className="banner">
            <strong>Link the new deal.</strong>
            <span>
              The new quote was made outside the portal — pick its Sales Pipeline deal and this
              renewal tracks it. Both deals gain the old↔new association fields.
            </span>
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label>Search the Sales Pipeline</label>
            <input className="input" style={{ width: '100%' }} placeholder="Deal name…" value={dealQuery}
              onChange={(e) => setDealQuery(e.target.value)} autoFocus />
          </div>
          <div style={{ marginTop: 8 }}>
            {dealResults.map((deal) => (
              <div key={deal.hsDealId} className="school">
                <div style={{ flex: 1 }}>
                  <strong>{deal.name}</strong>
                  <div className="tiny muted">
                    {deal.stageLabel ?? deal.stage ?? 'stage unknown'}
                    {deal.owner ? <> · {deal.owner}</> : null}
                    {deal.amount != null ? <> · {formatGBP(deal.amount)}</> : null}
                    {/* Decision 143: check the candidate in HubSpot before linking. */}
                    {' '}· {hubspotDealUrl(portalId, deal.hsDealId)
                      ? <a href={hubspotDealUrl(portalId, deal.hsDealId)!} target="_blank" rel="noreferrer">deal {deal.hsDealId} ↗</a>
                      : <>deal {deal.hsDealId}</>}
                  </div>
                </div>
                <button className="btn btn--sm" disabled={busy} title="Track this deal — the quote already lives in HubSpot."
                  onClick={() => run(async () => {
                    await linkRenewalDeal(r.id, deal.hsDealId)
                    setPanel('summary')
                    setNotice(`Linked to “${deal.name}”.`)
                  })}>Link</button>
                <button className="btn btn--sm btn--primary" disabled={busy}
                  title="Link AND start a W9R quote attached to this deal — its fields and customer pull in, and sending updates this deal rather than creating one."
                  onClick={() => { setTemplateFor({ dealId: deal.hsDealId, dealName: deal.name }); setPanel('template') }}
                  >Link + quote</button>
              </div>
            ))}
            {dealQuery.trim().length >= 2 && dealResults.length === 0 && (
              <p className="muted small">Nothing matches yet.</p>
            )}
          </div>
        </>
      )}

      {panel === 'linkQuote' && (
        <>
          <div className="banner">
            <strong>Link a portal quote.</strong>
            <span>
              The renewal quote was built in the ordinary builder and this row never learned about
              it. Find it below and the renewal adopts it — it keeps its number; its HubSpot deal
              gains the old↔new association. If the quote is already <strong>accepted</strong>,
              linking records the renewal at once: the contract deal moves to “New Contract - In PRJ”.
            </span>
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label>Search quotes (number, title or customer)</label>
            <input className="input" style={{ width: '100%' }} placeholder="e.g. W9Q-2026-0142 or Sheldon" value={quoteQuery}
              onChange={(e) => setQuoteQuery(e.target.value)} autoFocus />
          </div>
          <div style={{ marginTop: 8 }}>
            {quoteResults.map((q) => (
              <div key={q.id} className="school">
                <div style={{ flex: 1 }}>
                  <strong>{q.number}</strong>
                  {' '}<span className={`pill tiny${q.status === 'accepted' ? ' pill--ok' : ''}`}>{q.status}</span>
                  <div className="small">{q.title ?? <span className="muted">(untitled)</span>}</div>
                  <div className="tiny muted">
                    {q.customerName ?? 'no customer'} · {q.partnerName}
                    {q.value != null ? <> · {formatGBP(q.value)}</> : null}
                    {q.acceptedAt ? <> · accepted {formatUKDate(q.acceptedAt)}</> : q.sentAt ? <> · sent {formatUKDate(q.sentAt)}</> : null}
                    {q.hsDealId ? (hubspotDealUrl(portalId, q.hsDealId)
                      ? <> · <a href={hubspotDealUrl(portalId, q.hsDealId)!} target="_blank" rel="noreferrer">deal {q.hsDealId} ↗</a></>
                      : <> · deal {q.hsDealId}</>) : <> · no deal yet</>}
                  </div>
                </div>
                <button className="btn btn--sm btn--primary" disabled={busy}
                  title={q.status === 'accepted'
                    ? 'Adopt this quote AND record the renewal — the acceptance already happened.'
                    : 'Adopt this quote — its acceptance will record the renewal when it comes.'}
                  onClick={() => run(async () => {
                    const res = await linkRenewalQuote(r.id, q.id)
                    setPanel('summary')
                    setNotice(res.renewed
                      ? `Linked ${res.number} — accepted, so the renewal is recorded and the contract deal is moving to In PRJ.`
                      : `Linked ${res.number} — accepting it will record the renewal.`)
                  })}>Link</button>
              </div>
            ))}
            {quoteQuery.trim().length >= 2 && quoteResults.length === 0 && (
              <p className="muted small">Nothing matches — only live quotes (draft, sent, viewed, accepted) not already tied to a renewal are offered.</p>
            )}
          </div>
        </>
      )}

      {panel === 'template' && (
        <>
          <div className="banner">
            <strong>Start from a template?</strong>
            <span>
              {templateFor
                ? <>The quote will attach to “{templateFor.dealName}”. </>
                : null}
              A template brings its sections and lines — priced from today’s catalogue, never the
              old contract — plus whatever cover note, footer and T&amp;Cs it carries. The customer
              and site still seed from the contract being renewed.
            </span>
          </div>
          <div style={{ marginTop: 10 }}>
            <div className="school">
              <div style={{ flex: 1 }}>
                <strong>Blank quote</strong>
                <div className="tiny muted">One empty section — build it from scratch.</div>
              </div>
              <button className="btn btn--sm" disabled={busy}
                onClick={() => void startRenewalQuote(null)}>Start blank</button>
            </div>
            {(templates ?? []).map((t) => (
              <div key={t.id} className="school">
                <div style={{ flex: 1 }}>
                  <strong>{t.name}</strong>
                  {t.partnerId === null && <span className="chip" style={{ marginLeft: 6 }}>Wave9</span>}
                  <div className="tiny muted">
                    {t.summary} · {t.carries.label}
                  </div>
                </div>
                <button className="btn btn--sm btn--primary" disabled={busy}
                  onClick={() => void startRenewalQuote(t.id)}>Use</button>
              </div>
            ))}
            {templates != null && templates.length === 0 && (
              <p className="muted small" style={{ marginTop: 8 }}>
                No templates yet — save one from any quote (Builder → Save as template).
              </p>
            )}
            {templates == null && <p className="muted small" style={{ marginTop: 8 }}>Loading templates…</p>}
          </div>
        </>
      )}

      {panel === 'activity' && (
        <>
          {(events ?? []).map((e, i) => (
            <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid var(--line-soft)' }}>
              <div className="small"><strong>{EVENT_LABELS[e.kind] ?? e.kind}</strong>{e.actor ? <span className="muted"> — {e.actor}</span> : null}</div>
              {e.kind === 'note'
                ? <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{String(e.detail.text ?? '')}</div>
                : null}
              <div className="tiny muted">
                {formatUKDate(e.at)}
                {e.kind !== 'note' && Object.keys(e.detail).length > 0 && <> · {summariseDetail(e.detail)}</>}
              </div>
            </div>
          ))}
          {events != null && events.length === 0 && <p className="muted small">No activity yet.</p>}
          {events == null && <p className="muted small">Loading…</p>}
        </>
      )}
    </SlideOver>
  )
}

const EVENT_LABELS: Record<string, string> = {
  quote_superseded: 'Renewal quote superseded — the row follows the new reference',
  appeared: '⛁ Entered the renewal window',
  quote_created: '＋ Renewal quote created',
  deal_linked: '⛓ Existing deal linked',
  quote_linked: '⛓ Existing portal quote linked',
  rolled_over: '↻ Rolled over',
  renewed: '✓ Renewed',
  lost: '✕ Not renewing',
  reopened: '⟲ Reopened',
  note: '✎ Note',
  marked_at_risk: '⚠ Marked at risk',
  outcome_from_hubspot: '⇣ Outcome picked up from HubSpot',
  rollover_email_sent: '✉ Rollover email sent',
  rollover_email_skipped: '✉ Rollover email NOT sent',
  hubspot_updated: '⇡ HubSpot updated',
  hubspot_error: '⚠ HubSpot update failed',
}

function summariseDetail(detail: Record<string, unknown>): string {
  return Object.entries(detail)
    .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
    .slice(0, 4)
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(' · ')
}
